// RequestsListPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import utils from '../../utils';
import { GrAdd, GrSearch } from 'react-icons/gr';
import { FiDownload, FiPlusCircle } from 'react-icons/fi';
import toast from 'react-hot-toast';
import RequestStats from '../../components/requests/RequestStats';
import adminServices from '../../services/AdminServices';
import FetchError from '../../components/feedback/FetchError';
import moment from 'moment';
import Pagination from '../../components/nav/Pagination';
import Empty from '../../components/feedback/Empty';
import Loading from '../../components/feedback/Loading';
import RequestModal from '../../components/requests/RequestModal';

function RequestsListPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState();
    const [requests, setRequests] = useState();
    const [action, setAction] = useState(0);
    const [activeRequest, setActiveRequest] = useState(null);

    const searchInputRef = useRef();
    const navigate = useNavigate();
    const location = useLocation();

    const currentStatus = searchParams.get('status') || '';
    const currentUrgency = searchParams.get('urgency_level') || '';
    const currentOrdering = searchParams.get('ordering') || '';
    const currentSearch = searchParams.get('search') || '';

    useEffect(() => {
        const params = searchParams.toString();
        getRequests(params);
    }, [searchParams]);
    useEffect(() => { }, [activeRequest])

    const getRequests = (params) => {
        setError();
        setLoading(true);
        adminServices.getRequests(params ? `?${params}` : '')
            .then(res => {
                setRequests(res.data);
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                setError(err);
                setLoading(false);
            });
    };

    const handleSearch = (e) => {
        if (e.key === 'Enter') {
            const query = e.target.value.trim();
            setSearchParams(prev => {
                const newParams = new URLSearchParams(prev);
                if (query) {
                    newParams.set('search', query);
                } else {
                    newParams.delete('search');
                }
                newParams.delete('page');
                return newParams;
            });
        }
    };

    const updateURL = (newParams) => {
        navigate({
            pathname: location.pathname,
            search: newParams.toString(),
        }, { replace: true });
    };

    const handleStatusChange = (e) => {
        const value = e.target.value;
        const params = new URLSearchParams(searchParams);
        if (value) {
            params.set('status', value);
        } else {
            params.delete('status');
        }
        params.delete('page');
        updateURL(params);
    };

    const handleUrgencyChange = (e) => {
        const value = e.target.value;
        const params = new URLSearchParams(searchParams);
        if (value) {
            params.set('urgency_level', value);
        } else {
            params.delete('urgency_level');
        }
        params.delete('page');
        updateURL(params);
    };

    const handleOrderingChange = (e) => {
        const value = e.target.value;
        const params = new URLSearchParams(searchParams);
        if (value) {
            params.set('ordering', value);
        } else {
            params.delete('ordering');
        }
        params.delete('page');
        updateURL(params);
    };

    const changePageParam = (page) => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            if (page) {
                newParams.set('page', page);
            } else {
                newParams.delete('page');
            }
            return newParams;
        });
    };

    const downloadCsv = () => {
        toast.error("Export feature coming soon");
    };

    const url = window.location.href;

    function openModal(action, user = null) {
        const openBtn = document.getElementById("request-create-open-btn");
        setAction(action);
        setActiveRequest(user);
        if (openBtn) openBtn.click();
    }

    return (
        <div className="mb-16">
            <Helmet>
                <title>Requests - {utils.siteName}</title>
                <link rel="canonical" href={url} />
                <meta property="description" content="Manage and triage patient connection requests." />
            </Helmet>

            <div className="container mx-auto space-y-5">
                <RequestStats />
                <RequestModal
                    action={action}
                    activeRequest={activeRequest}
                    onReload={getRequests}
                    setActiveRequest={setActiveRequest}
                />
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-zinc-900 tracking-tight">Requests Management</h1>
                        <p className="text-zinc-500 mt-1">Manage and triage incoming patient connection requests.</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={downloadCsv}
                            className="hidden items-center justify-center px-4 py-2 bg-white dark:bg-zinc-800 border border-border-light dark:border-zinc-700 rounded-lg text-sm font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary shadow-sm transition-all"
                        >
                            <FiDownload className="inline mr-2 text-[20px]" />
                            Export CSV
                        </button>
                        <button
                            onClick={() => openModal(0)}
                            className="inline-flex items-center justify-center px-4 py-2 bg-primary hover:bg-sky-600 border border-transparent rounded-lg text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary shadow-sm transition-all">
                            <GrAdd className="inline mr-2 text-[20px]" />
                            Add New Record
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-surface-light p-4 rounded-xl border border-border-light">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                        {/* Search */}
                        <div className="">
                            <label className="sr-only" htmlFor="request-search">Search</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <GrSearch className="text-zinc-400 text-[20px]" />
                                </div>
                                <input
                                    className="block w-full pl-10 pr-3 py-2.5 border border-border-light rounded-lg bg-zinc-50 text-zinc-900 placeholder-zinc-500 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
                                    id="request-search"
                                    name="request-search"
                                    placeholder="Search by patient, nurse, or skill"
                                    type="text"
                                    defaultValue={currentSearch}
                                    onKeyDown={handleSearch}
                                    ref={searchInputRef}
                                />
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row gap-4">
                            {/* Status Filter */}
                            <div className="">
                                <div className="relative">
                                    <select
                                        className="block w-full pl-3 pr-10 py-2.5 text-base border-border-light focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-lg bg-zinc-50 text-zinc-700 appearance-none border"
                                        value={currentStatus}
                                        onChange={handleStatusChange}
                                    >
                                        <option value="">All Statuses</option>
                                        <option value="Pending">Pending</option>
                                        <option value="Matched">Matched</option>
                                        <option value="Accepted">Accepted</option>
                                        <option value="Cancelled">Cancelled</option>
                                        <option value="Completed">Completed</option>
                                    </select>
                                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-zinc-500">
                                        <FiPlusCircle className="text-[20px]" />
                                    </div>
                                </div>
                            </div>

                            {/* Urgency Filter */}
                            <div className="">
                                <div className="relative">
                                    <select
                                        className="block w-full pl-3 pr-10 py-2.5 text-base border-border-light focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-lg bg-zinc-50 text-zinc-700 appearance-none border"
                                        value={currentUrgency}
                                        onChange={handleUrgencyChange}
                                    >
                                        <option value="">All Urgency</option>
                                        <option value="Low">Low</option>
                                        <option value="Medium">Medium</option>
                                        <option value="Urgent">Urgent</option>
                                    </select>
                                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-zinc-500">
                                        <FiPlusCircle className="text-[20px]" />
                                    </div>
                                </div>
                            </div>

                            {/* Ordering */}
                            <div className="">
                                <div className="relative">
                                    <select
                                        className="block w-full pl-3 pr-10 py-2.5 text-base border-border-light focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-lg bg-zinc-50 text-zinc-700 appearance-none border"
                                        value={currentOrdering}
                                        onChange={handleOrderingChange}
                                    >
                                        <option value="">Sort By</option>
                                        <option value="-creation_time">Newest First</option>
                                        <option value="creation_time">Oldest First</option>
                                        <option value="-last_updated_time">Recently Updated</option>
                                        <option value="urgency_level">Urgency (A-Z)</option>
                                        <option value="-urgency_level">Urgency (Z-A)</option>
                                    </select>
                                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-zinc-500">
                                        <FiPlusCircle className="text-[20px]" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {error && <FetchError error={error} />}

                {loading ? (
                    <Loading />
                ) : (
                    <div>
                        <div className="bg-surface-light dark:bg-surface-dark rounded-xl border border-border-light dark:border-border-dark overflow-hidden">
                            <div className='overflow-x-auto'>
                                <table className="table table-compact table-zebra w-full">
                                    <thead>
                                        <tr>
                                            <th>Patient</th>
                                            <th className='hidden md:table-cell'>Nurse</th>
                                            <th className='hidden md:table-cell'>Location</th>
                                            <th className='hidden md:table-cell'>Urgency</th>
                                            <th className='hidden md:table-cell'>Care Needs</th>
                                            <th className='hidden md:table-cell'>Created</th>
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {requests?.results?.length > 0 ? (
                                            requests.results.map((request) => (
                                                <tr key={request.id}>
                                                    <td>
                                                        <Link to={`/patient/${request?.patient?.id}`}  className="flex gap-3 items-center group hover:cursor-pointer">
                                                            <div className="hidden md:avatar placeholder">
                                                                {request?.patient?.user?.image ? (
                                                                    <div className="w-16 rounded-full">
                                                                        <img src={request?.patient.user.image} alt={request?.patient.user.full_name} />
                                                                    </div>
                                                                ) : (
                                                                    <div className="bg-neutral text-white rounded-full w-16">
                                                                        <span className="text-lg">
                                                                            {utils.makeProfileLetter(request?.patient.user)}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div>
                                                                {request?.patient?.user?.full_name && (
                                                                    <p className="font-bold group-hover:underline">{request?.patient.user.full_name}</p>
                                                                )}
                                                                <p className="text-gray-600">
                                                                    {request?.patient?.user?.phone_number ? (
                                                                        <span>{request?.patient?.user?.phone_number}</span>
                                                                    ):(
                                                                        <span>
                                                                            {request?.patient?.user?.email}
                                                                        </span>
                                                                    )}
                                                                </p>
                                                            </div>
                                                        </Link>
                                                    </td>
                                                    <td className='hidden md:table-cell'>
                                                        {request.get_assigned_nurse ? (
                                                            <Link to={`/nurse/${request.get_assigned_nurse.nurse?.id}`} className='flex gap-3 items-center group hover:cursor-pointer'>
                                                                <div className="hidden md:avatar placeholder">
                                                                    {request.get_assigned_nurse.nurse?.user?.image ? (
                                                                        <div className="w-16 rounded-full">
                                                                            <img
                                                                                src={request.get_assigned_nurse.nurse.user.image}
                                                                                alt={request.get_assigned_nurse.nurse.user.full_name}
                                                                            />
                                                                        </div>
                                                                    ) : (
                                                                        <div className="bg-neutral text-white rounded-full w-16 flex items-center justify-center">
                                                                            <span className="text-lg">
                                                                                {utils.makeProfileLetter(request.get_assigned_nurse.nurse.user)}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div>
                                                                    <p className="font-bold group-hover:underline">{request.get_assigned_nurse.nurse.user.full_name}</p>
                                                                    <p className="text-gray-600">
                                                                        {request.get_assigned_nurse.nurse.user.phone_number ? (
                                                                            <span>{request.get_assigned_nurse.nurse.user.phone_number}</span>
                                                                        ):(
                                                                            <span>{request.get_assigned_nurse.nurse.user.email}</span>
                                                                        )}</p>
                                                                </div>
                                                            </Link>
                                                        ) : '—'}
                                                    </td>
                                                    <td className='hidden md:table-cell'>
                                                        {request.location_preference?.address}
                                                    </td>

                                                    <td className='hidden md:table-cell'>
                                                        {request.urgency_level}
                                                    </td>
                                                    <td className='hidden md:table-cell'>
                                                        {(() => {
                                                            const skills = request.requested_skills || [];
                                                            if (skills.length === 0) return '—';
                                                            const displayed = skills.slice(0, 2).map(s => s.name);
                                                            return skills.length > 2 ? displayed.join(', ') + ', ...' : displayed.join(', ');
                                                        })()}
                                                    </td>
                                                    <td className='hidden md:table-cell'>
                                                        {moment(request.creation_time).format("MMM Do YYYY")}
                                                    </td>
                                                    <td>
                                                        <span className={`px-2 py-1 rounded text-xs ${request.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                                                            request.status === 'Matched' ? 'bg-blue-100 text-blue-800' :
                                                                request.status === 'Accepted' ? 'bg-purple-100 text-purple-800' :
                                                                    request.status === 'Completed' ? 'bg-green-100 text-green-800' :
                                                                        'bg-red-100 text-red-800'
                                                            }`}>
                                                            {request.status}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <button
                                                            onClick={() => openModal(1, request)}
                                                            className="btn btn-secondary btn-sm">
                                                            View
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : null}
                                    </tbody>
                                </table>

                                {(!requests || requests.results?.length === 0) && !loading && (
                                    <div className="p-3">
                                        <Empty objectName="Requests" />
                                    </div>
                                )}
                            </div>
                        </div>

                        {!loading && !error && requests && requests.count > 0 && (
                            <div className="flex justify-center mt-7">
                                <Pagination
                                    count={requests.count}
                                    next={requests.next}
                                    previous={requests.previous}
                                    changePageParam={changePageParam}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default RequestsListPage;